import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import toast from "react-hot-toast";
import { 
    useAdminRoles, 
    useCreateRole, 
    useUpdateRole,
    useDeleteRole,
} from "@/services/adminRoleService";
import type { Role, RolePayload } from "@/services/adminRoleService";

import { Button } from "@/components/ui/button";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Switch } from "@/components/ui/switch";
import { Edit2, Plus, Trash2, Shield, Loader2, Search } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";

// Schema Validation
const formSchema = z.object({
    role_name: z.string().min(1, "Nama role wajib diisi"),
    description: z.string().optional().nullable(),
    category: z.string().optional().nullable(),
    is_active: z.boolean().default(true),
});
type FormValues = z.infer<typeof formSchema>;

export default function RolesPage() {
    const { data: roles, isLoading } = useAdminRoles();
    const createMutation = useCreateRole();
    const updateMutation = useUpdateRole();
    const deleteMutation = useDeleteRole();

    // Client-side pagination and search state
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [search, setSearch] = useState("");

    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [editingRole, setEditingRole] = useState<Role | null>(null);

    // Alert dialog state
    const [isAlertOpen, setIsAlertOpen] = useState(false);
    const [alertConfig, setAlertConfig] = useState<{
        title: string;
        description: string;
        actionText: string;
        action: () => void;
        isDestructive?: boolean;
    } | null>(null);

    // Client-side filtering and pagination logic
    const filteredRoles = (roles || []).filter(role => 
        role.role_name.toLowerCase().includes(search.toLowerCase()) || 
        role.description?.toLowerCase().includes(search.toLowerCase())
    );
    const totalItems = filteredRoles.length;
    const totalPages = Math.ceil(totalItems / perPage) || 1;
    const paginatedRoles = filteredRoles.slice((page - 1) * perPage, page * perPage);
    const fromItem = totalItems === 0 ? 0 : (page - 1) * perPage + 1;
    const toItem = Math.min(page * perPage, totalItems);

    const activeRolesCount = (roles || []).filter(r => r.is_active).length;
    const inactiveRolesCount = (roles || []).filter(r => !r.is_active).length;

    const form = useForm<FormValues>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            role_name: "",
            description: "",
            category: "",
            is_active: true,
        },
    });

    const isMutating = createMutation.isPending || updateMutation.isPending;

    const handleOpenDialog = (role?: Role) => {
        if (role) {
            setEditingRole(role);
            form.reset({
                role_name: role.role_name,
                description: role.description || "",
                category: role.category || "",
                is_active: Boolean(role.is_active),
            });
        } else {
            setEditingRole(null);
            form.reset({
                role_name: "",
                description: "",
                category: "",
                is_active: true,
            });
        }
        setIsDialogOpen(true);
    };

    const onSubmit = (values: FormValues) => {
        const payload: RolePayload = {
            role_name: values.role_name,
            description: values.description || null,
            category: values.category || null,
            is_active: values.is_active,
        };

        if (editingRole) {
            updateMutation.mutate(
                { id: editingRole.role_id, payload },
                {
                    onSuccess: () => {
                        toast.success("Role berhasil diperbarui");
                        setIsDialogOpen(false);
                    },
                    onError: (error: any) => {
                        toast.error(error?.response?.data?.message || "Gagal memperbarui role");
                    },
                }
            );
        } else {
            createMutation.mutate(payload, {
                onSuccess: () => {
                    toast.success("Role berhasil ditambahkan");
                    setIsDialogOpen(false);
                },
                onError: (error: any) => {
                    toast.error(error?.response?.data?.message || "Gagal menambahkan role");
                },
            });
        }
    };

    const handleDelete = (id: number) => {
        setAlertConfig({
            title: "Hapus Role",
            description: "Apakah Anda yakin ingin menghapus role ini? Tindakan ini tidak dapat dibatalkan.",
            actionText: "Hapus",
            isDestructive: true,
            action: () => {
                deleteMutation.mutate(id, {
                    onSuccess: () => toast.success("Role berhasil dihapus"),
                    onError: (error: any) => toast.error(error?.response?.data?.message || "Gagal menghapus role"),
                });
            }
        });
        setIsAlertOpen(true);
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-[50vh]">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto space-y-8 pb-20 px-4 md:px-0">
            {/* --- HEADER --- */}
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 bg-white dark:bg-slate-900 p-6 md:p-8 rounded-3xl shadow-sm border border-slate-100 dark:border-slate-800">
                <div>
                    <div className="flex items-center gap-3 mb-2">
                        <div className="p-2.5 rounded-xl bg-indigo-50 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400">
                            <Shield className="w-6 h-6" />
                        </div>
                        <h1 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">Kelola Peran</h1>
                    </div>
                    <p className="text-sm text-slate-500 dark:text-slate-400 max-w-xl">
                        Manajemen hak akses dan peran pengguna dalam sistem.
                    </p>
                </div>
                <div className="flex items-center gap-3 shrink-0 flex-wrap">
                    <Button onClick={() => handleOpenDialog()} className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl h-11 px-6 font-bold shadow-md transition-all">
                        <Plus className="w-4 h-4 mr-2" /> Tambah Peran
                    </Button>
                </div>
            </div>

            {/* --- SUMMARY CARDS --- */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm flex flex-col justify-between h-[100px] relative overflow-hidden group">
                    <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                        <Shield className="w-12 h-12 text-indigo-500" />
                    </div>
                    <p className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest flex items-center gap-1.5"><Shield className="w-4 h-4 text-indigo-500" /> Total Peran</p>
                    <p className="text-3xl font-black text-slate-900 dark:text-white">{(roles || []).length}</p>
                </div>
                <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm flex flex-col justify-between h-[100px] relative overflow-hidden group">
                    <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                        <Shield className="w-12 h-12 text-emerald-500" />
                    </div>
                    <p className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-emerald-500"></div> Aktif</p>
                    <p className="text-3xl font-black text-slate-900 dark:text-white">{activeRolesCount}</p>
                </div>
                <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm flex flex-col justify-between h-[100px] relative overflow-hidden group">
                    <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                        <Shield className="w-12 h-12 text-rose-500" />
                    </div>
                    <p className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-rose-500"></div> Nonaktif</p>
                    <p className="text-3xl font-black text-slate-900 dark:text-white">{inactiveRolesCount}</p>
                </div>
            </div>

            {/* --- TOOLBAR (FILTER & SEARCH) --- */}
            <div className="flex flex-col gap-4 w-full">
                <div className="flex flex-wrap items-center gap-3 w-full">
                    <div className="relative flex-1 min-w-[250px]">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <input
                            type="text"
                            placeholder="Cari peran atau deskripsi..."
                            value={search}
                            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                            className="w-full h-11 pl-11 pr-4 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl text-sm focus:ring-2 focus:ring-indigo-500 outline-none shadow-sm transition-all font-medium"
                        />
                    </div>
                </div>
            </div>

            {/* --- DATA TABLE --- */}
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden relative">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm whitespace-nowrap">
                            <thead className="bg-slate-50 dark:bg-slate-800/50 text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider text-[11px]">
                                <tr>
                                    <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800 w-[50px] text-center">No</th>
                                    <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800">Nama Role</th>
                                    <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800">Kategori</th>
                                    <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800">Deskripsi</th>
                                    <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800 text-center">Status</th>
                                    <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800 text-right sticky right-0 z-10 bg-slate-50 dark:bg-slate-800/90 shadow-[-10px_0_15px_-10px_rgba(0,0,0,0.1)]">Aksi</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                {!paginatedRoles || paginatedRoles.length === 0 ? (
                                    <tr>
                                        <td colSpan={6} className="p-12 text-center text-slate-500">
                                            Belum ada data peran.
                                        </td>
                                    </tr>
                                ) : (
                                    paginatedRoles.map((role, index) => (
                                        <tr key={role.role_id} className="group hover:bg-slate-50/50 dark:hover:bg-slate-800/20 transition-colors bg-white dark:bg-slate-900">
                                            <td className="px-6 py-4 text-center font-medium">{(page - 1) * perPage + index + 1}</td>
                                            <td className="px-6 py-4 font-semibold text-slate-900 dark:text-slate-100">
                                                {role.role_name}
                                            </td>
                                            <td className="px-6 py-4">
                                                {role.category ? (
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-[11px] font-bold bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                                                        {role.category}
                                                    </span>
                                                ) : (
                                                    "-"
                                                )}
                                            </td>
                                            <td className="px-6 py-4 text-slate-500 whitespace-normal min-w-[200px]">
                                                {role.description || "-"}
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                                    role.is_active 
                                                        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' 
                                                        : 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-300'
                                                }`}>
                                                    {role.is_active ? 'Aktif' : 'Nonaktif'}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-right sticky right-0 z-10 bg-white dark:bg-slate-900 group-hover:bg-slate-50/50 dark:group-hover:bg-slate-800/80 shadow-[-10px_0_15px_-10px_rgba(0,0,0,0.1)] transition-colors">
                                                <div className="flex justify-end gap-2">
                                                    <Button
                                                        variant="outline"
                                                        size="icon"
                                                        title="Edit"
                                                        className="h-8 w-8 text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg"
                                                        onClick={() => handleOpenDialog(role)}
                                                    >
                                                        <Edit2 className="h-4 w-4" />
                                                    </Button>
                                                    <Button
                                                        variant="outline"
                                                        size="icon"
                                                        title="Hapus"
                                                        className="h-8 w-8 text-rose-600 hover:text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded-lg"
                                                        onClick={() => handleDelete(role.role_id)}
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination Controls matching Conference style */}
                    {totalItems > 0 && (
                        <div className="p-4 md:p-5 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm">
                            <div className="flex items-center gap-3 text-slate-500 font-medium">
                                <span>Menampilkan {fromItem} - {toItem} dari {totalItems} data</span>
                                <div className="h-4 w-px bg-slate-300 dark:bg-slate-700 hidden sm:block"></div>
                                <div className="flex items-center gap-2 sm:flex">
                                    <label htmlFor="perPage">Tampilkan:</label>
                                    <select
                                        id="perPage"
                                        value={perPage}
                                        onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
                                        className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md px-2 py-1 outline-none focus:ring-2 focus:ring-primary font-bold"
                                    >
                                        <option value="10">10</option>
                                        <option value="25">25</option>
                                        <option value="50">50</option>
                                        <option value="100">100</option>
                                    </select>
                                </div>
                            </div>
                            
                            {totalPages > 1 && (
                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setPage(p => Math.max(1, p - 1))}
                                        disabled={page === 1}
                                        className="rounded-lg"
                                    >
                                        Sebelumnya
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                        disabled={page === totalPages}
                                        className="rounded-lg"
                                    >
                                        Selanjutnya
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}
            </div>

            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogContent className="sm:max-w-[425px]">
                    <DialogHeader>
                        <DialogTitle>{editingRole ? "Edit Role" : "Tambah Role Baru"}</DialogTitle>
                    </DialogHeader>

                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 pt-4">
                            <FormField
                                control={form.control}
                                name="role_name"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Nama Role <span className="text-red-500">*</span></FormLabel>
                                        <FormControl>
                                            <Input placeholder="Contoh: Admin" {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            
                            <FormField
                                control={form.control}
                                name="category"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Kategori (Opsional)</FormLabel>
                                        <FormControl>
                                            <Input placeholder="Contoh: System" {...field} value={field.value || ""} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="description"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Deskripsi (Opsional)</FormLabel>
                                        <FormControl>
                                            <Textarea 
                                                placeholder="Keterangan mengenai role ini" 
                                                className="resize-none"
                                                {...field} 
                                                value={field.value || ""}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="is_active"
                                render={({ field }) => (
                                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4 mt-2">
                                        <div className="space-y-0.5">
                                            <FormLabel className="text-base">Status Aktif</FormLabel>
                                            <div className="text-sm text-muted-foreground">
                                                Tentukan apakah role ini dapat digunakan
                                            </div>
                                        </div>
                                        <FormControl>
                                            <Switch
                                                checked={field.value}
                                                onCheckedChange={field.onChange}
                                            />
                                        </FormControl>
                                    </FormItem>
                                )}
                            />

                            <div className="flex gap-3 pt-4 justify-end">
                                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="rounded-xl h-11 px-6 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800">
                                    Batal
                                </Button>
                                <Button type="submit" disabled={isMutating} className="rounded-xl h-11 px-6 bg-indigo-600 hover:bg-indigo-700 text-white font-bold shadow-md">
                                    {isMutating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                    Simpan
                                </Button>
                            </div>
                        </form>
                    </Form>
                </DialogContent>
            </Dialog>

            {/* Custom Alert Dialog for Confirmations */}
            <AlertDialog open={isAlertOpen} onOpenChange={setIsAlertOpen}>
                <AlertDialogContent className="rounded-3xl border-slate-200 dark:border-slate-800 p-6 shadow-2xl max-w-sm">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="text-xl font-bold text-slate-900 dark:text-white">
                            {alertConfig?.title}
                        </AlertDialogTitle>
                        <AlertDialogDescription className="text-slate-500 dark:text-slate-400">
                            {alertConfig?.description}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="mt-6 gap-2">
                        <AlertDialogCancel className="rounded-xl h-11 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 font-medium">Batal</AlertDialogCancel>
                        <AlertDialogAction 
                            onClick={() => {
                                alertConfig?.action();
                                setIsAlertOpen(false);
                            }}
                            className={`rounded-xl h-11 font-bold text-white shadow-md ${
                                alertConfig?.isDestructive 
                                    ? "bg-rose-600 hover:bg-rose-700" 
                                    : "bg-indigo-600 hover:bg-indigo-700"
                            }`}
                        >
                            {alertConfig?.actionText}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
